import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { ETestIds } from '../../../../constants/testIds';
import { useIsDesktop } from '../../../../hooks/common';
import { setActiveMasterButton } from '../../../../redux/landingSlice';
import { EFillApplicationStickyButtonType } from '../types';

const safeObserve = (observer: IntersectionObserver, element: Element | null) => {
  if (element) {
    observer.observe(element);
  }
};

interface IUseMobileStickyButtonsOptions {
  enabled?: boolean;
}

export const useMobileStickyButtons = (ref: HTMLElement | null, options?: IUseMobileStickyButtonsOptions) => {
  const dispatch = useDispatch();
  const isDesktop = useIsDesktop();
  const isFiltersVisible = useRef<boolean>(true);
  const isPresentationBtnVisible = useRef<boolean>(true);
  const enabled = options?.enabled ?? true;

  useEffect(() => {
    if (isDesktop || !enabled) {
      return;
    }

    // Сбрасываем состояние при инициализации
    dispatch(setActiveMasterButton(null));

    const presentationBtn = document.querySelector(`[data-testid=${ETestIds.PresentationBtn}]`);
    const showOffersBtn = document.querySelector(`[data-testid=${ETestIds.ShowOffersButton}]`);
    const documentBody = document.body as HTMLBodyElement;
    const controlsContainer = document.querySelector(`[data-testid=${ETestIds.ControlsContainer}]`);
    const seoBlock =
      document.querySelector(`[data-testid=${ETestIds.InformationalSeo}]`) ||
      document.querySelector(`[data-testid=${ETestIds.SeoMarkedList}]`);

    /**
     * Костыль для того, чтобы sticky кнопки не перекрывали футер, убрать после релиза фичи
     * */
    documentBody.style.paddingBottom = '110px';

    const topButtonObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            isPresentationBtnVisible.current = true;
            dispatch(setActiveMasterButton(null));
          } else {
            isPresentationBtnVisible.current = false;
            dispatch(setActiveMasterButton('showOffers'));
          }
        });
      },
      {
        threshold: 0.1,
        root: null,
      },
    );

    const bottomButtonObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (!isPresentationBtnVisible.current) {
              dispatch(setActiveMasterButton('showOffers'));
            }
          } else {
            if (!isFiltersVisible.current) {
              dispatch(setActiveMasterButton(EFillApplicationStickyButtonType.TOP));
            }
          }
        });
      },
      {
        threshold: 0.1,
        root: null,
      },
    );

    const controlsContainerObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            isFiltersVisible.current = true;
          } else {
            isFiltersVisible.current = false;
          }
        });
      },
      { threshold: 0.1, root: null },
    );

    const seoBlockObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !isFiltersVisible.current) {
            dispatch(setActiveMasterButton(EFillApplicationStickyButtonType.TOP));
          }
        });
      },
      { threshold: 0.1, root: null },
    );

    safeObserve(topButtonObserver, presentationBtn);
    safeObserve(bottomButtonObserver, showOffersBtn);
    safeObserve(controlsContainerObserver, controlsContainer);
    safeObserve(seoBlockObserver, seoBlock);

    return () => {
      topButtonObserver.disconnect();
      bottomButtonObserver.disconnect();
      controlsContainerObserver.disconnect();
      seoBlockObserver.disconnect();
    };
  }, [isDesktop, dispatch, ref, enabled]);
};
